/**
 * Revise Session Context — the "Revise with AI" both-open flag.
 *
 * A lightweight, inert-by-default coordinator flag. It exists so the chat ↔ notifications
 * mutual-exclusivity coordinator (useChatNotificationExclusivity, Workstream 6) can YIELD during an
 * active Revise session — the Revise 50/50 layout (Workstream 7) is the sole state where the chat
 * and the notifications panel are both open at once. Holds NO 50/50 layout and NO Revise button —
 * only the flag, the target approval id, and the revise conversation id (that UI is Workstream 7).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface ReviseSession {
  /** True while a Revise session is live — the sole chat+notifications both-open state. */
  reviseActive: boolean;
  /**
   * Begin a Revise session for a pending approval (opens the 50/50 in Workstream 7).
   * `conversationId` is the (temp, `conversation-*`) id of the chat this session opens — it is
   * later swapped for the real id via `setReviseConversationId` once the create resolves.
   *
   * STRICT single-flight (design §7.5, concurrency-review root fix): while a session is already
   * active — for ANY approval, including a re-click on THIS SAME approval, and regardless of
   * whether that session's own create has resolved yet — this is a clean no-op and returns `false`.
   * The caller (EmailDraftReviewCard) must not proceed to `createConversation`/dispatch a seed when
   * it gets `false` back. This replaces the old "supersede a DIFFERENT approval" behavior: starting a
   * second revise while one is open no longer force-ends the first — the caller closes the first one
   * explicitly (Send / Save & send / Discard, or the reconciler's close/switch backstop) before a new
   * one can begin. That is the accepted, design-consistent tradeoff for eliminating the overlapping
   * revise-create races (double-click same draft, revise-A-then-B before A resolves, re-revise a
   * still-open draft — all collided on the single-slot session state and/or ConversationContext's
   * unconditional force-navigate-on-create-resolve).
   *
   * Returns `true` when the session actually started.
   */
  beginRevise(approvalId: string, conversationId: string): boolean;
  /**
   * End the Revise session — returns to strict single-open. Idempotent. Threads the ending
   * approval id through a `xavior:revise-end` window event so listeners with no direct reference
   * to this context (the revise-seed listener in useChatWindowEvents) can react to the end —
   * specifically, drop it from their per-approval seed dedupe (and any still-pending seed for it) so
   * a re-revise on the SAME draft seeds again instead of being silently treated as already-seeded
   * (#5412).
   */
  endRevise(): void;
  /** The pending approval being revised, or null when no session is active. */
  activeApprovalId: string | null;
  /**
   * The conversation id hosting the active revise session — the temp id `beginRevise` was called
   * with until the seed listener confirms the create resolved and swaps in the real id via
   * `setReviseConversationId`. Null when no session is active.
   */
  reviseConversationId: string | null;
  /**
   * Swap in the real conversation id once the temp→real create resolves. No-op once the session
   * has already ended, so a lagging create for a closed session can't resurrect a tracked id.
   */
  setReviseConversationId(conversationId: string): void;
  /**
   * True when `conversationId` (the tempId `beginRevise` was invoked with, OR the real id it later
   * resolved to) is STILL the one the CURRENT session is tracking. Stable (never changes identity)
   * and always reads the live ref, so a caller can capture it once (e.g. inside the click handler
   * that started this exact create) and invoke it much later, from a closure that has long since
   * outlived any render — specifically, `ConversationContext.createConversation`'s `onSuccess`,
   * evaluated at RESOLVE time, to decide whether a Revise-with-AI create that may have been abandoned
   * (the user closed it, or re-opened the SAME draft as a brand-new session with a NEW tempId) should
   * still activate/navigate, or be inert (concurrency-review residual — a stale resolve must never
   * hijack the view away from whatever the user is looking at now, nor reactivate a dead conversation
   * nobody asked for).
   */
  isReviseConversationActive(conversationId: string): boolean;
}

const ReviseSessionContext = createContext<ReviseSession | undefined>(undefined);

export function ReviseSessionProvider({ children }: { children: ReactNode }) {
  const [activeApprovalId, setActiveApprovalId] = useState<string | null>(null);
  const [reviseConversationId, setReviseConversationIdState] = useState<string | null>(null);

  // Kept fresh every render (not in an effect) so `endRevise` — a useCallback with an EMPTY dep
  // array, deliberately stable so useReviseSessionReconciler's effect never re-fires merely because
  // the provider re-rendered — can still read the CURRENT approval id when it fires.
  const activeApprovalIdRef = useRef(activeApprovalId);
  activeApprovalIdRef.current = activeApprovalId;
  // Same freshness contract as activeApprovalIdRef — read by the create-failed listener below, which
  // (like endRevise) must be a stable callback that never re-subscribes on every provider re-render.
  const reviseConversationIdRef = useRef(reviseConversationId);
  reviseConversationIdRef.current = reviseConversationId;

  const dispatchReviseEnd = useCallback((approvalId: string) => {
    window.dispatchEvent(new CustomEvent('xavior:revise-end', { detail: { approvalId } }));
  }, []);

  const beginRevise = useCallback((approvalId: string, conversationId: string): boolean => {
    // Strict single-flight (§7.5, concurrency-review root fix): a session already active — for ANY
    // approval, including a re-click on THIS one — makes this a clean no-op. The refs are mutated
    // SYNCHRONOUSLY right here (not only via setState, which a caller can't observe until the next
    // render) so a double-click that invokes this twice in the SAME tick — before React has re-
    // rendered the now-disabled Revise button — is still caught: the second call sees the ref this
    // call just set. See the interface doc above for why this replaces the old cross-approval
    // supersede behavior.
    if (activeApprovalIdRef.current !== null) {
      return false;
    }
    activeApprovalIdRef.current = approvalId;
    reviseConversationIdRef.current = conversationId;
    setActiveApprovalId(approvalId);
    setReviseConversationIdState(conversationId);
    return true;
  }, []);

  const endRevise = useCallback(() => {
    const endingApprovalId = activeApprovalIdRef.current;
    activeApprovalIdRef.current = null;
    reviseConversationIdRef.current = null;
    setActiveApprovalId(null);
    setReviseConversationIdState(null);
    if (endingApprovalId) {
      dispatchReviseEnd(endingApprovalId);
    }
  }, [dispatchReviseEnd]);

  const setReviseConversationId = useCallback((conversationId: string) => {
    // A lagging create resolution for an already-ended session must not resurrect a tracked id.
    if (activeApprovalIdRef.current === null) return;
    reviseConversationIdRef.current = conversationId;
    setReviseConversationIdState(conversationId);
  }, []);

  // See the interface doc above — reads the LIVE ref so a closure captured once (at create-call time)
  // stays correct no matter how long it sits before being invoked at create-RESOLVE time.
  const isReviseConversationActive = useCallback(
    (conversationId: string) => reviseConversationIdRef.current === conversationId,
    [],
  );

  // A failed create must not leave the session stuck on a temp id that will never resolve to a real
  // one — ConversationContext dispatches this when `createConversationMutation` errors, carrying the
  // ORIGIN tempId of the failed create. Only reset when that tempId is the one THIS session is
  // currently tracking (still unresolved — `reviseConversationId` starts as the temp id at
  // `beginRevise` and is only ever swapped for a real id on a CONFIRMED create), so a failed create
  // for a superseded/already-ended/different session can never touch an unrelated active one.
  useEffect(() => {
    const onCreateFailed = (event: CustomEvent<{ tempId?: string }>) => {
      const failedTempId = event.detail?.tempId;
      if (!failedTempId || reviseConversationIdRef.current !== failedTempId) return;
      endRevise();
    };
    window.addEventListener('xavior:conversation-create-failed', onCreateFailed as EventListener);
    return () => {
      window.removeEventListener('xavior:conversation-create-failed', onCreateFailed as EventListener);
    };
  }, [endRevise]);

  const value = useMemo<ReviseSession>(
    () => ({
      reviseActive: activeApprovalId !== null,
      beginRevise,
      endRevise,
      activeApprovalId,
      reviseConversationId,
      setReviseConversationId,
      isReviseConversationActive,
    }),
    [
      activeApprovalId,
      reviseConversationId,
      beginRevise,
      endRevise,
      setReviseConversationId,
      isReviseConversationActive,
    ],
  );

  return <ReviseSessionContext.Provider value={value}>{children}</ReviseSessionContext.Provider>;
}

export function useReviseSession(): ReviseSession {
  const context = useContext(ReviseSessionContext);
  if (!context) {
    throw new Error('useReviseSession must be used within a ReviseSessionProvider');
  }
  return context;
}
